import { createSelector } from 'reselect';

import { extractTemplate } from 'wanderu-utils';

import {
    selectAppStateSlice,
} from '../../selectors';
import {
    isNonUsCanada,
    getImagesForPlace,
    mapContent,
    mapCarrierData,
    mapInfoSectionContent,
    mapQuickviewData,
    mapBlogLinksToImages,
    remapImageKeyToSrc,
} from '../../utils';
import NAMESPACE from './types';

const BASE_URL = 'https://www.wanderu.com';

function getPlaceGuideData(appState, placeGuideId) {
    return selectAppStateSlice(appState)[NAMESPACE][placeGuideId];
}

function getPlaceGuideStats(appState, placeGuideId) {
    const placeGuideData = getPlaceGuideData(appState, placeGuideId);
    return placeGuideData && placeGuideData.stats;
}

function getMetricsData(appState, placeGuideId) {
    const placeGuideStats = getPlaceGuideStats(appState, placeGuideId);
    return placeGuideStats && placeGuideStats.metrics;
}

function getPlaceGuideContent(appState, placeGuideId) {
    const placeGuideData = getPlaceGuideData(appState, placeGuideId);
    return placeGuideData && placeGuideData.content;
}

function getLocationData(appState, placeGuideId) {
    const placeGuideData = getPlaceGuideData(appState, placeGuideId);
    return placeGuideData && placeGuideData.location;
}

function getCityToCityData(appState, placeGuideId) {
    const placeGuideData = getPlaceGuideData(appState, placeGuideId);
    return placeGuideData && placeGuideData.c2c;
}

function getNoIndex(appState, placeGuideId) {
    const placeGuideData = getPlaceGuideData(appState, placeGuideId);
    return placeGuideData && placeGuideData.no_index;
}

function getNoIndexLocale(appState, placeGuideId) { 
    const placeGuideData = getPlaceGuideData(appState, placeGuideId);
    return placeGuideData?.no_index_locale ?? undefined;
}

function getPopularStationsData(appState, placeGuideId) {
    const placeGuideStats = getPlaceGuideStats(appState, placeGuideId);
    return placeGuideStats && placeGuideStats.mostPopular && placeGuideStats.mostPopular.stations;
}

function getPlaceGuideCarrierData(appState, placeGuideId) {
    const placeGuideData = getPlaceGuideData(appState, placeGuideId);
    return placeGuideData && placeGuideData.carriers;
}

function getRouteData(appState, placeGuideId) {
    const placeGuideData = getPlaceGuideData(appState, placeGuideId);
    return placeGuideData && placeGuideData.routes;
}

function getAlternateTransModes(appState, placeGuideId) {
    const cityGuideData = getPlaceGuideData(appState, placeGuideId);
    return cityGuideData && cityGuideData.alternate_trans_modes;
}

function getLongFormContent(appState, placeGuideId) {
    const placeGuideContent = getPlaceGuideContent(appState, placeGuideId);
    return extractTemplate(placeGuideContent);
}

function getCarrierContent(appState, placeGuideId) {
    const carriers = getPlaceGuideCarrierData(appState, placeGuideId) || [];

    return carriers.reduce((acc, carrier) => {
        Object.entries(carrier.content)
            .forEach(([key, value]) => {
                acc[`${carrier.code}_${key}`] = value;
            });
        return acc;
    }, {});
}

function getCityToCityContent(appState, placeGuideId) {
    const c2cs = getCityToCityData(appState, placeGuideId) || [];

    return c2cs.reduce((acc, c2c) => {
        Object.entries(c2c.content)
            .forEach(([key, value]) => {
                acc[`${c2c.depart_wcityid}_${key}`] = value;
            });
        return acc;
    }, {});
}

function getBreadcrumbUrls(appState, placeGuideId, urlParams) {
    const {
        mode,
        state,
        country,
        region = '',
    } = urlParams;

    const countryCode = country || region.split('-')[0];

    let breadcrumbUrls = {
        transMode: `/${mode}-tickets/`,
    };

    if (state || region) {
        breadcrumbUrls = {
            ...breadcrumbUrls,
            toCountry: `/${mode}-tickets/${countryCode}/`,
        };
    }

    if (region) {
        breadcrumbUrls = {
            ...breadcrumbUrls,
            toState: `/${mode}-tickets/${region}/`,
        };
    }

    return breadcrumbUrls;
}

function getMostPopularRoutes(appState, placeGuideId) {
    const stats = getPlaceGuideStats(appState, placeGuideId) || {};

    if (stats.mostPopular) {
        return typeof stats.mostPopular.routes === 'object'
            ? stats.mostPopular.routes
            : [];
    }

    return [];
}

function mapBreadcrumbData(content, locationData = {}, breadcrumbUrls) {
    if (!content || !locationData) {
        return;
    }

    let breadCrumbData = {
        transMode: {
            name: content.transModeTickets,
            url: breadcrumbUrls.transMode,
        },
        activeIndex: {
            name: locationData.countryName,
        },
    };

    if (breadcrumbUrls.toCountry) {
        breadCrumbData = {
            ...breadCrumbData,
            toCountry: {
                code: locationData.countryCode,
                name: locationData.countryName,
                url: breadcrumbUrls.toCountry,
            },
            activeIndex: {
                name: locationData.stateName,
            },
        };
    }

    if (breadcrumbUrls.toState) {
        breadCrumbData = {
            ...breadCrumbData,
            toState: {
                code: locationData.stateCode,
                name: locationData.stateName,
                url: breadcrumbUrls.toState,
            },
            activeIndex: {
                name: locationData.name,
            },
        };
    }

    return breadCrumbData;
}

function mapBreadcrumbStructuredData(params, content, breadcrumbData) {
    if (!breadcrumbData) {
        return;
    }

    const {
        locale,
    } = params;
    const {
        transMode,
        toCountry = {},
        toState = {},
        activeIndex,
    } = breadcrumbData;

    if (!transMode.name) {
        return;
    }

    const itemListElement = [
        {
            '@type': 'ListItem',
            position: 1,
            name: transMode.name,
            item: `${BASE_URL}/${locale}${transMode.url}`,
        },
        {
            '@type': 'ListItem',
            position: 2,
            name: toCountry.name || activeIndex.name,
            item: toCountry.url
                ? `${BASE_URL}/${locale}${toCountry.url}`
                : `${BASE_URL}${content.canonicalSlug}`,
        },
    ];

    if (breadcrumbData.toCountry) {
        itemListElement.push({
            '@type': 'ListItem',
            position: 3,
            name: toState.name || activeIndex.name,
            item: toState.url
                ? `${BASE_URL}/${locale}${toState.url}`
                : `${BASE_URL}${content.canonicalSlug}`,
        });
    }

    if (breadcrumbData.toState) {
        itemListElement.push({
            '@type': 'ListItem',
            position: 4,
            name: activeIndex.name,
            item: `${BASE_URL}${content.canonicalSlug}`,
        });
    }

    return [{
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement,
    }];
}

function mapCheapestTicketData(data) {
    if (!data) {
        return;
    }

    const {
        cheapestPriceUsd,
        cheapestAveragePriceUsd,
        savingsPriceUsd,
        minPriceUsd,
        avgPriceUsd,
        maxPriceUsd,
    } = data;

    return {
        avgPrice: avgPriceUsd,
        cheapestPrice: cheapestPriceUsd,
        cheapestAvgPrice: cheapestAveragePriceUsd,
        maxPrice: maxPriceUsd,
        minPrice: minPriceUsd,
        savingsPriceUsd: savingsPriceUsd,
    };
}

function mapPopularStationsData(stationData) {
    return stationData;
}

function mapCityToCityData(data) {
    if (!data) {
        return;
    }

    return data.map((c2c) => {
        return {
            departCityId: c2c.depart_wcityid,
            departCity: c2c.depart_city_name,
            averagePriceUsd: c2c.avg_price_usd,
            averageTimeMin: c2c.avg_time_min,
            c2cUrl: c2c.c2c_url,
            content: c2c.content,
        };
    });
}

function mapInterlinkData(placeGuideData) {
    return {
        ...placeGuideData?.interlinks,
        states: isNonUsCanada(placeGuideData?.location)
            ? undefined
            : placeGuideData?.interlinks?.states,
    };
}

// no particular mapping needed here
const selectLocationData = getLocationData;
const selectAlternateTransModes = getAlternateTransModes;
const selectMostPopularRoutes = getMostPopularRoutes;
const selectRouteData = getRouteData;
const selectLongFormContent = getLongFormContent;
const selectNoIndex = getNoIndex;
const selectNoIndexLocale = getNoIndexLocale;
const selectStats = getPlaceGuideStats;

const selectInterlinks = createSelector(
    getPlaceGuideData,
    mapInterlinkData,
);

const selectBreadcrumbData = createSelector(
    getPlaceGuideContent,
    getLocationData,
    getBreadcrumbUrls,
    mapBreadcrumbData,
);

const selectCheapestTicketData = createSelector(
    getMetricsData,
    mapCheapestTicketData,
);

const selectC2CData = createSelector(
    getCityToCityData,
    mapCityToCityData,
);

const selectContent = createSelector(
    getPlaceGuideContent,
    getCityToCityContent,
    getCarrierContent,
    mapContent,
);

const selectInfoSectionContent = createSelector(
    getPlaceGuideContent,
    mapInfoSectionContent,
);

const selectPopularStationsData = createSelector(
    getPopularStationsData,
    mapPopularStationsData,
);

const selectPlaceGuideCarrierData = createSelector(
    getPlaceGuideCarrierData,
    mapCarrierData,
);

const selectQuickviewData = createSelector(
    getMetricsData,
    mapQuickviewData,
);

const selectStructuredData = createSelector(
    (appState, placeId, params) => params,
    getPlaceGuideContent,
    selectBreadcrumbData,
    mapBreadcrumbStructuredData,
);

const selectBlogLinks = createSelector(
    getPlaceGuideData,
    (placeGuideData) => placeGuideData?.blogLinks,
);

const selectImages = createSelector(
    getPlaceGuideData,
    (placeGuideData) => placeGuideData?.images,
);

const selectBlurbSectionData = createSelector(
    selectBlogLinks,
    selectPopularStationsData,
    (rawBlogLinks, popularStations = []) => {
        const blogLinks = rawBlogLinks || [];

        // todo map image information for cities and stations once it is available

        const blogPosts = mapBlogLinksToImages(blogLinks);

        return {
            blogPosts,
            departStations: popularStations,
        };
    },
);

const selectBlurbSectionContent = createSelector(
    selectBlurbSectionData,
    selectPopularStationsData,
    selectImages,
    (blurbSectionData = {}, stationsData = {}, images = {}) => {
        const {
            blogPosts,
            arriveCityImages,
            arriveStationImages,
            arriveStations = [],
            departStations = [],
        } = blurbSectionData;

        const departCityImages = remapImageKeyToSrc(getImagesForPlace({ type: 'city', images }));
        const departStationImages = remapImageKeyToSrc(getImagesForPlace({ type: 'station', images }));

        return {
            blogPosts,
            departCityImages,
            arriveCityImages,
            departStationImages,
            arriveStationImages,
            departStations,
            departMainStationContent: stationsData.mainDepart?.popularStationsDepartureDescription,
            arriveStations,
            arriveMainStationContent: stationsData.mainArrive?.popularStationsArrivalDescription,
        };
    },
);

export {
    selectAlternateTransModes,
    selectBlurbSectionContent,
    selectBreadcrumbData,
    selectC2CData,
    selectCheapestTicketData,
    selectNoIndex,
    selectNoIndexLocale,
    selectContent,
    selectInfoSectionContent,
    selectInterlinks,
    selectLocationData,
    selectLongFormContent,
    selectMostPopularRoutes,
    selectPlaceGuideCarrierData,
    selectPopularStationsData,
    selectQuickviewData,
    selectRouteData,
    selectStats,
    selectStructuredData,
};
